import React from "react";

export type CardShadow = "none" | "xs" | "sm" | "custom";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Tailwind class for background color. Default matches Figma: #f8fafc
   */
  bgClass?: string;
  /**
   * Tailwind class for border color/width. Default matches Figma: 1px solid #e5e7eb
   */
  borderClass?: string;
  /**
   * Tailwind class for border radius. Default matches Figma: 12px
   */
  roundedClass?: string;
  /**
   * Shadow preset. "xs" matches Figma Drop Shadow/xs (0 1px 1px rgba(0,0,0,0.05)).
   * "custom" lets you provide a custom Tailwind arbitrary value via shadowClass.
   */
  shadow?: CardShadow;
  /**
   * Tailwind class for a custom shadow (only used when shadow="custom").
   * Example: shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]
   */
  shadowClass?: string;
  /** Additional classes appended at the end */
  className?: string;
  children?: React.ReactNode;
}

/**
 * Reusable Card UI component focused on color, border, and shadow.
 * Defaults are aligned with the Figma design:
 * - Background: #f8fafc (bg-gray-50)
 * - Border: 1px solid #e5e7eb (border border-gray-200)
 * - Radius: 12px (rounded-[12px])
 * - Shadow: 0 1px 1px 0 rgba(0,0,0,0.05) (shadow-xs/custom)
 */
export default function Card({
  bgClass = "bg-gray-50",
  borderClass = "border border-gray-200",
  roundedClass = "rounded-xl",
  shadow = "custom",
  shadowClass = "shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]",
  className = "",
  children,
  ...rest
}: CardProps) {
  const shadowCn =
    shadow === "none"
      ? ""
      : shadow === "xs"
        ? "shadow-xs"
        : shadow === "sm"
          ? "shadow-sm"
          : shadowClass; // custom

  return (
    <div
      className={`${bgClass} ${borderClass} ${roundedClass} ${shadowCn} ${className}`.trim()}
      {...rest}
    >
      {children}
    </div>
  );
}
